import { DEG_TO_RAD, Fitout, STARTING_FITOUT } from "./Fitout";
import { Hull, HullSize, mountPoint } from "./Ships";

/**
 * The guns a captain sails with, and the flight of what they fire.
 *
 * A roster is one ship's battery: the two cannon slots of the boat it was bought as, each
 * holding whichever gun type its captain equipped, resolved at the level they have climbed to.
 * Everything about a gun — where it is bolted, which way it points, how far and how fast it
 * shoots, how long it takes to reload — is authored in the dashboard's Boat panel
 * (design/Settings.md) rather than typed here.
 *
 * **It used to be one list for the whole arena**, because every ship was the starting boat.
 * Captains buy different boats and grow them now (design/Evolution.md, design/Shop.md), so a
 * roster is built per ship out of its `Fitout` and rebuilt when it levels. Both ends do it from
 * the same loadout and level off the wire, and they must agree: the server owns the shots, and
 * the client draws them from the same numbers so a ball is at the same place on both screens
 * between patches. See design/Networking.md.
 *
 * **A slot's bearing is where a gun rests, not where it points.** Guns swing now: the pointer
 * names a direction and every gun comes onto it as far as its arc and its traverse allow, which
 * is [Aiming.ts](Aiming.ts)'s job and not this file's. A roster is still the static half — where
 * a gun is bolted, which way it faces with nobody aiming it, and what its shot does — and a
 * `Battery` is the moving half laid over it. See design/Controls.md.
 */

/** One gun as the simulation uses it: a place on the deck, a bearing, and its shot's numbers. */
/**
 * Shortest gap between two balls leaving one ship, in seconds, however many guns are loaded.
 *
 * The trigger is held rather than clicked (design/Controls.md), and without this a press with a
 * full battery would empty it inside three frames — a click and a held button would be the same
 * input, and the reload dials in `config/boat.json` would only ever be felt as one long wait
 * between salvoes. With it, a tap is one ball and a held trigger is a rolling fire that runs down
 * the battery gun by gun.
 *
 * It is a decision rather than a dial: it is the cadence of the **ship**, not a property of any
 * gun, and every gun's own rate still applies on top of it. Loose enough that the starting boat's
 * two guns never touch it — they are reload-bound at half a second apart.
 *
 * **It lives here, in `shared/`, because two things now honour it.** The arena does not enforce it —
 * it would happily take a whole broadside in one step — so it is a convention that every hull in the
 * sea keeps of its own accord: the browser client's `_workTrigger`, and a bot's own gunnery. A bot
 * reading a copy of this number would be a bot whose rate of fire drifted from a captain's the first
 * time somebody re-judged it.
 */
export const TRIGGER_INTERVAL = 0.15;

export interface Cannon {
    /**
     * Which of the hull's cannon slots this gun sits in. What a `fire` message names, and what
     * a cooldown is keyed by on both ends.
     *
     * It is the **slot** index, not a position in the roster. Every boat has exactly two and
     * neither moves during a life, so the two are the same number today — it stays the slot's
     * because that is what the wire and the reload clocks are keyed by, and a roster that could
     * ever be built short would otherwise renumber the gun it dropped.
     */
    index: number;
    /** Where it is bolted, in hull-local fractions — see `mountPoint` in [Ships.ts](Ships.ts). */
    localX: number;
    localY: number;
    /**
     * Which way it **rests**, in radians relative to the bow. Port is -PI/2, starboard +PI/2.
     *
     * The bearing a gun has with nobody aiming it, and the centre of the arc it swings through —
     * never assume a ball left on it. Where a gun is actually pointing is `Battery.bearingOf`.
     */
    bearing: number;
    /**
     * How fast it comes round on its mount, in **radians per second** — the tree authors it in
     * degrees (`CannonStats.rotationSpeed`), and this is the one place that conversion happens.
     * Zero is a gun welded to its slot, which is what every gun was before aiming existed.
     */
    traverse: number;
    /** How far its shot carries before it falls into the sea, in world pixels. */
    range: number;
    /** How fast its shot travels, in world pixels per second. */
    speed: number;
    /**
     * What fraction of a victim's hold one of its balls takes — `CannonStats.steal`, the number
     * that used to be one global `HIT_STEAL_FRACTION` for the whole game (design/Battle.md).
     *
     * It is on the roster rather than looked up at impact because a ball carries it: the shot is
     * worth what the gun that fired it was worth at the moment it fired, not what its captain has
     * since grown into. See `Shot.steal` in `server/rooms/BattleRoom.ts`.
     */
    steal: number;
    /** How long this gun takes to reload, in **seconds** — the tree stores it in milliseconds. */
    reload: number;
    /** How long one of its shots is in the air, in seconds. `range / speed`, precomputed. */
    flightSeconds: number;
    /**
     * Where the ball leaves this gun, in **world pixels from the slot**, measured in the gun's
     * own turned frame: `muzzleAcross` to its starboard, `muzzleAlong` toward its breech —
     * negative is out of the barrel, the same sign the bow has.
     *
     * Resolved here rather than at firing time because it is the one place that has all three
     * parts of it: the type's `muzzleX`/`muzzleY` are fractions of the gun's **drawn** length,
     * and the drawn length is the type's `size` times the slot's own `scale` times the hull's.
     * Doing it per shot would mean carrying the slot and the fitout into `muzzle()` for a
     * multiplication that only changes when a captain levels.
     */
    muzzleAcross: number;
    muzzleAlong: number;
    /**
     * How long this gun is actually drawn, in world pixels — the type's `size`, the slot's
     * `scale` and the hull's own. The length `muzzleAcross` and `muzzleAlong` are fractions of,
     * kept because the
     * muzzle flash is measured in the same unit and would otherwise have to re-derive it from a
     * fitout the client no longer has to hand. See `MuzzleFlash.scale` in [Fx.ts](Fx.ts).
     */
    drawn: number;
}

/**
 * Every gun on a ship, in slot order.
 *
 * Two is the normal length and `CANNON_SLOTS` is why, but **empty is still a legal roster and
 * not an error**: a catalogue with no guns in it is a thing the dashboard lets you author, and
 * everything downstream — the header's battery strip, the gun a click picks, the server's fire
 * handler — reads the length rather than assuming. A click on an unarmed boat finds no gun and
 * does nothing.
 *
 * The combat numbers come off `FittedCannon.stats` rather than off the type, because a type
 * stores what a gun is at level 1 and what a level is *worth* rather than what this particular
 * gun does — [Fitout.ts](Fitout.ts) evaluates that once per ship. Only the art, the size and the
 * muzzle point are the type's own, and none of those change with a level.
 */
export function rosterFor(fitout: Fitout): Cannon[] {
    return fitout.cannons.map((fitted) => {
        // The gun as it is actually drawn: the type's size, nudged by whatever the slot asks for,
        // then grown with the hull. A spawn point authored against the art therefore rides the gun
        // when the type, the slot or the captain's level scales it. See `CannonType.muzzleX` in
        // BoatTree.ts, and `drawnSize` in src/scripts/boats/Deck.ts — the client draws the gun at
        // this same length, and a disagreement is a muzzle flash beside the barrel it came out of.
        const drawn = fitted.fitting.size * fitted.slot.scale * fitout.scale;

        return {
            index: fitted.index,
            localX: fitted.slot.x,
            localY: fitted.slot.y,
            bearing: fitted.slot.rotation * DEG_TO_RAD,
            // Degrees on the way in, radians from here on, exactly as the reload converts its
            // milliseconds: the simulation runs in radians and a gun that swings 60x too fast is
            // the same class of mistake. See `CannonStep.rotationSpeedStep` in BoatTree.ts.
            traverse: fitted.stats.rotationSpeed * DEG_TO_RAD,
            range: fitted.stats.range,
            speed: fitted.stats.projectileSpeed,
            steal: fitted.stats.steal,
            // Milliseconds on the way in, seconds from here on: the simulation and every
            // per-frame path in the client are in seconds, and mixing the two is how a reload
            // ends up a thousand times too long. See `CannonStep.cooldownDrop` in BoatTree.ts.
            reload: fitted.stats.cooldown / 1000,
            flightSeconds: fitted.stats.projectileSpeed > 0 ? fitted.stats.range / fitted.stats.projectileSpeed : 0,
            muzzleAcross: fitted.fitting.muzzleX * drawn,
            muzzleAlong: fitted.fitting.muzzleY * drawn,
            drawn
        };
    });
}

/** The battery the free boat carries at level 1. What the header opens a battle with. */
export const STARTING_CANNONS: readonly Cannon[] = rosterFor(STARTING_FITOUT);

/**
 * The gun in `roster` sitting in slot `index`, or null. The server trusts nothing a client
 * sends, including whether the slot it named is a slot on this boat or has a gun in it at all.
 */
export function cannonAt(roster: readonly Cannon[], index: unknown): Cannon | null {
    if (typeof index !== "number" || !Number.isInteger(index)) return null;
    return roster.find((cannon) => cannon.index === index) ?? null;
}

/**
 * Where a shot from `cannon` starts and which way it goes, for a hull at a given place and
 * facing.
 *
 * Two offsets stack, and they are different kinds of thing. The **slot** puts the gun on the
 * deck, in fractions of the hull it is bolted to — `size` is the firing ship's own hull's, and a
 * Galleon's rail is a long way further out than a Sloop's. The **muzzle** then walks from the
 * slot to the point on the gun the ball actually leaves, in fractions of that gun's drawn
 * length, turned with the gun rather than with the ship.
 *
 * The muzzle offset defaults to nothing, which is where it sat before it was authorable: at rung
 * 1 a gun is 52px long against a 190px hull, so barrel length is a few pixels of smoke, and a
 * ball spawned well ahead of the rail is already inside a ship that is alongside. It is a dial
 * in the Boat panel's cannon page for the cases where that is the wrong trade — a long chase gun
 * whose ball visibly starts at the breech. See `CannonType.muzzleX` in BoatTree.ts.
 *
 * `bearing` is where the gun is **currently** pointing, relative to the bow — `Battery.bearingOf`
 * on whichever side is asking. It is a parameter rather than `cannon.bearing` because a gun
 * swings (design/Controls.md): the slot is only where it rests, and a ball that left on the rest
 * bearing while the barrel was drawn somewhere else is the drift this whole module exists to
 * prevent. It defaults to the rest bearing, which is the welded gun the arena used to have.
 *
 * The position is **not** wrapped — callers on the server wrap it as they write it into the
 * world, and the client wants the raw offset to draw against a camera. See design/Networking.md.
 */
export function muzzle(hull: Hull, cannon: Cannon, size: HullSize, bearing = cannon.bearing): { x: number; y: number; heading: number } {
    const point = mountPoint(hull, cannon.localX, cannon.localY, size);
    const heading = hull.heading + bearing;

    // The gun's own frame turned into the world's, by the same rotation `mountPoint` uses one
    // level up: +across is to starboard of the barrel, +along is toward its breech.
    const sin = Math.sin(heading);
    const cos = Math.cos(heading);

    return {
        x: point.x + cannon.muzzleAcross * cos - cannon.muzzleAlong * sin,
        y: point.y + cannon.muzzleAcross * sin + cannon.muzzleAlong * cos,
        heading
    };
}
